/**
 * Export ▾ and ⋯ — the tab bar's two menus (spec §B2). Both wrap the shared
 * Radix DropdownMenu: these ARE menus (choosing an item does one thing and
 * closes), unlike View ▾, which is a popover of switches.
 *
 * Only the TRIGGERS are dock-specific — `h-7` and outlined like every other
 * tab-bar tool, where the docks had `size="sm"` (h-8) with an `h-7` override.
 * The items are the plain `DropdownMenuItem` / `DropdownMenuSeparator` the
 * docks already render; they are re-exported here so a dock imports its
 * menus from one place.
 *
 * Remember the jsdom detail the dock tests rely on: a Radix DropdownMenu
 * trigger opens on `pointerdown`, not on `click`.
 */
import type { ReactNode } from 'react';

import { ChevronDown, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { DOCK_CONTROL } from './dockStyles';

export { DropdownMenuItem as DockMenuItem, DropdownMenuLabel as DockMenuLabel, DropdownMenuSeparator as DockMenuSeparator };

export interface DockExportMenuProps {
  /** The trigger's `data-testid` — `clearance-export`, `sv-export`. */
  testId?: string;
  /** Defaults to the shared `dock.export` copy. */
  label?: string;
  /** Controlled open state (the harness, a test). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** `DockMenuItem`s. */
  children: ReactNode;
}

export const DockExportMenu = ({ testId, label, open, onOpenChange, children }: DockExportMenuProps) => {
  const { t } = useTranslation('project');
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button type='button' data-testid={testId} className={DOCK_CONTROL.button}>
          {label ?? t('dock.export')}
          <ChevronDown className={DOCK_CONTROL.iconSm} aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' collisionPadding={8} className='text-xs'>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export interface DockOverflowMenuProps {
  /** The trigger's `data-testid` (`clearance-more`, `sv-more`). */
  testId?: string;
  /** The trigger's name; defaults to the shared `dock.more` copy. */
  label?: string;
  children: ReactNode;
}

/** ⋯ — the actions that do not earn a place in the bar (Re-run). */
export const DockOverflowMenu = ({ testId, label, children }: DockOverflowMenuProps) => {
  const { t } = useTranslation('project');
  const name = label ?? t('dock.more');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type='button' data-testid={testId} aria-label={name} title={name} className={DOCK_CONTROL.iconButton}>
          <MoreHorizontal className={DOCK_CONTROL.icon} aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' collisionPadding={8} className='text-xs'>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
